import { Activity } from '@/types/activity'
import { TripSummaryNote } from '@/types/note'
import { TripSummaryPackingList } from '@/types/packingList'
import { TripSummaryAccommodation } from '@/types/accommodation'
import { Photo } from '@/types/photo'
import {
  ExpenseAccount,
  Expense,
  PaidExpense,
  TripSummaryExpense,
} from '@/types/expense'
import { Transportation } from '@/types/transportation'
import { Document } from '@/types/document'
import { ProfileSubject, User } from '@/types/user'
import {
  LocationDataSource,
  NearbySearchDataSource,
  WaypointDayItemType,
  WaypointDataSource,
} from '@/utils/enums.js'

export interface TripDocument {
  id: string
}

export interface Location {
  id?: string
  city?: string
  country?: string
  countryCode?: string
  dataSource: string | number
  icon?: string
  latitude: number
  longitude: number
  name: string
  placeId: string
  postalCode?: string
  state?: string
  street?: string
  streetNumber?: string | number
  category?: string
  meta?: {
    summary: string
    images?: string[]
  }
}

export interface DateRange {
  type: 'SPECIFIC' | 'WHOLE_MONTH'
  value: string
}

export interface Category {
  icon: string
  id: string
  name: string
}

export interface Contact {
  phone?: string
  formattedPhone?: string
  twitter?: string
  facebook?: string
  facebookName?: string
  facebookUsername?: string
}

export interface Rating {
  count: number
  max: number
  value: number
}

export interface Review {
  author: string
  createdAt: Date
  photo: string
  text: string
}

export interface Metadata {
  categories?: Category[]
  contact?: Contact
  dataSource?: string
  externalId?: string
  location?: Location
  metaURL?: string
  photos?: string[]
  rating?: Rating
  reviews?: Review[]
  website?: string
}

export interface POILinkObject {
  // eslint-disable-next-line camelcase
  google_maps?: string
  foursquare?: string
  yelp?: string
}

export interface POIDay {
  createdAt?: string
  createdBy?: string
  date?: string
  dayId: string
  endTime?: string
  itemId: string
  nearbyPlace?: string
  note?: string
  startTime?: string
  type: string
  updatedAt?: string
  _id: string
}

export interface POIDayMeta {
  date?: string
  dayId?: string
}

export interface PointOfInterest {
  _id?: string
  name: string
  createdAt?: Date
  createdBy?: string
  location?: Location
  startDate?: DateRange
  endDate?: DateRange
  startTime?: number
  metadata?: Metadata
  days?: string[] | POIDayMeta[]
}

export interface Waypoint {
  _id: string
  bookings: any[]
  transportations: Transportation[]
  createdAt: Date
  createdBy: string
  location: Location
  name: string
  startDate?: DateRange
  endDate?: DateRange
  pointOfInterests: PointOfInterest[]
  activities: Activity[]
}

export interface WaypointSearchResult {
  source: number
  predictions: any[]
}

// Share Token Types -----------------------------------------------------------

/**
 * Privacy settings that can be applied to a share token
 */
export const privacySettings = [
  'activities',
  'photos',
  'pois',
  'accommodations',
] as const

export type PrivacySetting = (typeof privacySettings)[number]

export interface ShareToken {
  token: string
  name: string
  privacySettings: PrivacySetting[]
  isActive: boolean
}

export interface B2BProvider {
  type: 'b2b'
  tourOperator: {
    slug: string
    name: string
    logo: string
  }
}

export interface AERProvider {
  type: 'aer'
}

export interface TourlaneProvider {
  type: 'tourlane'
}

export type TripProvider = B2BProvider | AERProvider | TourlaneProvider

export interface Trip {
  _id: string
  id: string
  name: string
  createdBy: string | User
  accounts: ExpenseAccount[]
  attendees: User[]
  pendingAttendees: User[]
  leftAttendees: User[]
  documents: TripDocument[]
  expenses: Expense[]
  theme: string
  token: string
  shareTokens: ShareToken[]
  tokenIsPublic: boolean
  updatedAt: Date
  paid: PaidExpense[]
  waypoints: Waypoint[]
  waypointAlgorithm: number
  archived: boolean
  currency: string
  startDate?: DateRange
  endDate?: DateRange
  hasRoutes?: boolean
  activeProSubscription?: boolean
  organizationId?: string
  userInfo?: {
    useNewTransportationSystem?: boolean
  }
  provider?: TripProvider
}

export interface Route {
  _id: string
  trip: string
  type: string
  origin: string
  originLocation?: string
  destination: string
  destinationLocation?: string
  polyline: string
  alternatives: string[]
  booking?: string
  rawData: string
  properties: any
  alternativesProperties: any[]
  createdBy: string
}

export interface GpxFeature {
  type: string
  properties: {
    name: string
    time?: string
    _gpxType?: string
    coordinateProperties?: {
      times: string[]
    }
  }
  geometry: {
    type: 'LineString'
    coordinates: [number, number][]
  }
}

export interface GpxMultiLineFeature {
  type: string
  properties: {
    name: string
    time?: string
    _gpxType?: string
    coordinateProperties?: {
      times: string[]
    }
  }
  geometry: {
    type: 'MultiLineString'
    coordinates: [number, number][][]
  }
}

export interface GpxRoute {
  _id: string
  originName: string
  data: {
    type: string
    features: Array<GpxFeature | GpxMultiLineFeature>
  }
  createdAt: string
  color?: string
}

export interface DirectionProperties {
  originAddress: string
  destinationAddress: string
  distance: number
  duration: number
  departureDate?: string
}

export interface Direction {
  type: string
  origin: string
  originLocation?: string
  destination: string
  destinationLocation?: string
  polyline: string
  alternatives: string[]
  properties: DirectionProperties
  alternativesProperties: DirectionProperties[]
  rawData: string
}

export interface Temperature {
  average: number
  humidity: number
  maximum: number
  minimum: number
}

export interface ProFeature {
  title: string
  description: string
  img: string
  soon?: boolean
  new?: boolean
}

export interface WeatherSummary {
  description: string
  icon: string
  title: string
}

export interface Weather {
  summary: WeatherSummary
  temperature: Temperature
  url: string
}

export interface WeatherRecord {
  weather: Weather
  waypointId: string
  expiresAt: number
}

export interface PlaceRecommendation {
  dataSource: LocationDataSource
  distanceInMeters: number
  externalId: string
  location: Location
  name: string
  photo: string | null
}

export interface LocationSuggestionGeneral extends Location {
  dataSource: Exclude<LocationDataSource, LocationDataSource.GOOGLE>
}

export interface LocationSuggestionGoogle {
  dataSource: LocationDataSource.GOOGLE
  placeId: string
  type?: string
  label?: string
}

export type LocationSuggestion =
  | LocationSuggestionGoogle
  | LocationSuggestionGeneral

export interface NearbyPlaceAutocompleteResult {
  id: string
  dataSource: keyof typeof NearbySearchDataSource
  title: string
  subtitle: string
  titleHighlights: Array<{
    length: number
    offset: number
  }>
  subtitleHighlights: Array<{
    length: number
    offset: number
  }>
  location: LocationSuggestion
}

export interface LocationAutocompleteResultGeneral {
  id: string
  dataSource: Exclude<keyof typeof WaypointDataSource, 'GOOGLE'>
  title: string
  subtitle: string
  titleHighlights: Array<{
    length: number
    offset: number
  }>
  subtitleHighlights: Array<{
    length: number
    offset: number
  }>
  location: LocationSuggestionGeneral
  placeReference?: string
}

export interface LocationAutocompleteResultGoogle {
  id: string
  dataSource: 'GOOGLE'
  title: string
  subtitle: string
  titleHighlights: Array<{
    length: number
    offset: number
  }>
  subtitleHighlights: Array<{
    length: number
    offset: number
  }>
  location: LocationSuggestionGoogle
  placeReference?: string
}

export type LocationAutocompleteResult =
  | LocationAutocompleteResultGeneral
  | LocationAutocompleteResultGoogle

export interface DatePicker {
  dates: string[]
  months: string[]
  type: string
}

export interface WaypointDayItem {
  _id: string
  createdBy: string
  startTime?: string | null
  endTime?: string | null
  nearbyPlace?: PointOfInterest
  activity?: Activity
  note?: string
  type: string
}

export interface WaypointDayItemCreate {
  startTime?: string
  endTime?: string
  nearbyPlace?: PointOfInterest | string
  activity?: Activity
  note?: string
  type: string
  insertAt?: number
}

export interface WaypointDayItemUpdate {
  startTime?: string | null
  endTime?: string | null
  note?: string | null
}

export interface ActivityWaypointDayItem extends WaypointDayItem {
  type: WaypointDayItemType.ACTIVITY
  activity: Activity
}
export interface NearbyPlaceWaypointDayItem extends WaypointDayItem {
  type: WaypointDayItemType.NEARBY_PLACE
  nearbyPlace: PointOfInterest
}

export interface WaypointDay {
  _id: string
  createdAt: Date
  createdBy: string
  date: string
  color: string
  nearbyPlaces: PointOfInterest[]
  updatedAt: Date
  waypoint: string
  isVisible: boolean
  items?: WaypointDayItem[]
}

export interface TripPredictions {
  lambus: any[]
  external: any[]
}

export interface TripLink {
  embed: string
  url: string
}

export interface TripUpdateRelation {
  type: number
  tripId: string
  relation: {
    subject: ProfileSubject
    object: ProfileSubject | null
  }
}

export interface DashboardFeature {
  name: string
  md: number
  icon: string
  title: string
  isDisplayed: boolean
}

export interface WaypointDashboardFeature {
  name: string
  title: string
  isDisplayed: boolean
}

export interface TripSummary {
  transportation: Transportation[]
  expenses: TripSummaryExpense
  accommodation: TripSummaryAccommodation[]
  documents: Document[]
  photos: Photo[]
  notes: TripSummaryNote[]
  packing_lists: TripSummaryPackingList[]
}

export interface LinkedDay {
  date: string
  dayId: string
  itemId: string
  startTime?: string | null
  endTime?: string | null
}

export interface RearrangeWaypointDayItem {
  waypointId: string
  sourceDayId: string
  targetDayId: string
  sourceItemIndex: number
  targetItemIndex: number
}

export interface PointOfInterestPreview extends Metadata {
  name: string
}

export type ValidatePinCodeResponse =
  | {
      success: false
      code: number
    }
  | {
      available: true
    }

export interface CreateTripResponse {
  success: boolean
  trip?: Trip
}

export type JoinTripResponse =
  | {
      success: false
      code: number
    }
  | {
      success: true
      trip?: { _id: string }
    }

export interface ItineraryAttendee {
  id: string
  firstName: string
  lastName: string
  picture: string
}

export interface ItineraryTrip {
  id: string
  name: string
  startDate?: DateRange
  endDate?: DateRange
  attendees: ItineraryAttendee[]
}

export interface ItineraryDayItem {
  _id: string
  type: 'place' | 'activity' | 'unknown'
  itemId: string
  name: string
  subtitle: string
  icon?: string
  picture?: string
}

export interface ItineraryWaypointDay {
  _id: string
  date: string
  items: ItineraryDayItem[]
}

export interface ItineraryWaypoint {
  _id: string
  name: string
  picture?: string
  startDate?: DateRange
  endDate?: DateRange
  summary?: string | null
  days: ItineraryWaypointDay[]
  itemsWithoutDate: ItineraryDayItem[]
}

export interface ItineraryItem {
  waypoint: ItineraryWaypoint
}
